/**
 * Shared subscription-credit (מחסן והזמנות → הזמנות מנויים → זיכויים) types.
 *
 * A credit is a **manual financial document**, raised when a shaliach has to be credited for
 * a package that never arrived, wrong quantities, and so on. It is deliberately *not* a row
 * on `SubscriptionOrders`: an order is a frozen shipping document with no prices at all,
 * while a credit is nothing but a sum, a reason and an audit trail.
 *
 * `amount` is always **positive** — it is the sum to credit. The monthly account screen
 * (הנה"ח וכספים → תשלומים, not built yet) reads valid credits by `customerId` + `period` and
 * subtracts them from that month's charge. Nothing here writes to the גביה records, which are
 * a read-only projection of the Priority Excel imports.
 *
 * Like an order, a credit snapshots what it displays (`customerName`, `planName`, each item's
 * `name` and `unitPrice`), so re-pricing the catalog or renaming a customer never rewrites a
 * document that has already been credited.
 *
 * This file is dependency-free (no React/MUI/browser imports) so the backend can import it
 * via the `@malash-office/types/subscription-credits` deep alias.
 */
import { SelectOptionLite } from './customer.types';
import { effectiveUnitPrice, type PriceTier } from './subscription.types';

const toOptions = (values: readonly string[]): SelectOptionLite[] => values.map(v => ({ label: v, value: v }));

// --- סטטוס זיכוי ---
// As everywhere else in this project, the stored value *is* the Hebrew label. A credit is
// never edited or deleted — a mistake is cancelled, which keeps the document and its author
// on the record. Only a תקף credit counts towards the monthly account.
export const CREDIT_STATUS_VALID = 'תקף';
export const CREDIT_STATUS_CANCELLED = 'בוטל';

export const SUBSCRIPTION_CREDIT_STATUSES = [CREDIT_STATUS_VALID, CREDIT_STATUS_CANCELLED] as const;

export const SUBSCRIPTION_CREDIT_STATUS_OPTIONS = toOptions(SUBSCRIPTION_CREDIT_STATUSES);

/** Chip colour per status, as **theme palette keys** — this project never hardcodes a colour. */
export const SUBSCRIPTION_CREDIT_STATUS_COLOR: Record<string, 'success' | 'error' | 'default'> = {
  [CREDIT_STATUS_VALID]: 'success',
  [CREDIT_STATUS_CANCELLED]: 'error'
};

/**
 * One credited line. `unitPrice` is snapshotted from the catalog when the credit is raised —
 * the price the credit was calculated from must stay readable after the item is re-priced.
 */
export interface SubscriptionCreditItem {
  id: number;
  creditId: number;
  /** Null once the catalog item is deleted — `name` is what keeps the line readable. */
  planItemId: number | null;
  name: string;
  quantity: number;
  unitPrice: number;
}

export interface SubscriptionCredit {
  id: number;
  /** Kept for traceability and for the monthly account; the display reads `customerName`. */
  customerId: number | null;
  /** Snapshot of the shaliach / בית חב"ד name at the moment the credit was raised. */
  customerName: string;
  /** 'YYYY-MM-DD' — the date the credit is dated, which decides its `period`. */
  creditDate: string;
  /** 'YYYY-MM' — the monthly account this credit belongs to. Derived from `creditDate`. */
  period: string;
  planId: number | null;
  planName: string | null;
  /** Optional link to the order being credited, for traceability. */
  orderId: number | null;
  items: SubscriptionCreditItem[];
  /** Free-text reason. Either this, the items, or both — see `hasCreditReason`. */
  freeText: string | null;
  /** Always positive: the total to credit. */
  amount: number;
  status: string;
  createdBy?: { id: number; firstName: string; lastName: string } | null;
  cancelledBy?: { id: number; firstName: string; lastName: string } | null;
  cancelledAt?: string | null;
  createdAt?: string;
}

/** The minimum a credit total needs to know about one line. */
export interface CreditLineInput {
  quantity: number;
  unitPrice: number;
}

/**
 * Σ quantity × unitPrice over the chosen lines — what the amount field is *pre-filled* with.
 * The user may then override it (a partial credit, or a goodwill sum), which is why the
 * amount is stored on its own and never recomputed from the lines.
 */
export const calcCreditItemsTotal = (lines: readonly CreditLineInput[]): number =>
  lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);

/**
 * What one credited unit is worth. A line taken out of the shaliach's subscription is worth what
 * that subscription bills per unit — its price snapshot, at the *subscribed* quantity's הנחת כמות
 * (or the price agreed by hand). Crediting 100 of a weekly 1000 must not price them at the
 * 100-unit tier: the shaliach paid the 1000-unit price for them. Anything else falls back to the
 * catalog ladder at the credited quantity.
 *
 * The server's snapshot goes through it; the form previews the same two prices (the
 * subscription line's `unitPrice`, or `effectiveUnitPrice`), so the two agree.
 */
export const creditUnitPrice = (
  planItem: { price: number; priceTiers?: readonly PriceTier[] | null },
  quantity: number,
  subscriptionLine?: { unitPrice: number } | null
): number => (subscriptionLine ? Number(subscriptionLine.unitPrice) : effectiveUnitPrice(planItem, quantity));

/** 'YYYY-MM-DD' → 'YYYY-MM'. One implementation, used by the form preview and by the API. */
export const periodFromDate = (date: string): string => date.slice(0, 7);

/**
 * A credit must say *why*. Plan items and free text may be combined — the items describe
 * what was missing, the text explains the circumstance.
 */
export const hasCreditReason = (itemCount: number, freeText?: string | null): boolean =>
  itemCount > 0 || Boolean(freeText?.trim());

export const CREDIT_REASON_REQUIRED_MESSAGE = 'יש לבחור פריטי תוכנית או להזין טקסט חופשי';
export const CREDIT_AMOUNT_REQUIRED_MESSAGE = 'סכום לזיכוי חייב להיות גדול מאפס';

/** Payload guards, not business rules — one credit is a single incident, never a bulk run. */
export const CREATE_CREDIT_ITEM_LIMIT = 100;
export const CREDIT_AMOUNT_MAX = 999_999;
export const CREDIT_FREE_TEXT_MAX_LENGTH = 500;

/** One-line reason, for the table cell and the Excel export. */
export const creditReasonSummary = (credit: SubscriptionCredit): string =>
  [credit.items.map(item => `${item.name} × ${item.quantity}`).join(', '), credit.freeText?.trim()]
    .filter(Boolean)
    .join(' | ');

/** Query params for the server-side credit list (all optional, mirror the URL). */
export interface SubscriptionCreditListParams {
  page?: string | number;
  rowsPerPage?: string | number;
  sortField?: string;
  sortOrder?: string;
  /** Inclusive 'YYYY-MM-DD' bounds on `creditDate`. */
  startDate?: string;
  endDate?: string;
  /** 'YYYY-MM' — the monthly account. */
  period?: string;
  planId?: string | number;
  customerId?: string | number;
  /** Comma-separated — the status filter is a multi-select. */
  status?: string;
}

export interface SubscriptionCreditListResponse {
  rows: SubscriptionCredit[];
  count: number;
}

/** One line as the create request sends it. Name and price are resolved server-side. */
export interface CreateCreditItemBody {
  planItemId: number;
  quantity: number;
}

export interface CreateCreditBody {
  customerId: number;
  /** 'YYYY-MM-DD'; the server derives `period` from it. */
  creditDate: string;
  planId?: number | null;
  /**
   * The shaliach's subscription the lines are credited out of. Not stored — it only prices the
   * lines: see {@link creditUnitPrice}.
   */
  subscriptionId?: number | null;
  orderId?: number | null;
  items?: CreateCreditItemBody[];
  freeText?: string | null;
  amount: number;
}
